import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { LiquidMetalButton } from '../components/ui/liquid-metal-button';
import backgroundVideo from '../assets/Background.mp4';

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = {
      firstName: e.target.firstName.value,
      lastName: e.target.lastName.value,
      email: e.target.email.value,
      service: e.target.service.value,
      message: e.target.message.value,
    };

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        const errorData = await response.json();
        alert('Failed to send message: ' + (errorData.message || 'Server error'));
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('An error occurred while sending your message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-screen bg-black text-white flex flex-col justify-center items-center px-4 sm:px-8 lg:px-16 relative overflow-hidden">
      {/* Background Video */}
      <div className="absolute inset-0 w-full h-full z-0 pointer-events-none">
        <video
          src={backgroundVideo}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/40 to-black pointer-events-none" />
      </div>

      {/* Background glow */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none z-0" />
      <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-green-500/5 rounded-full blur-[120px] pointer-events-none z-0" />

      <div className="max-w-7xl w-full mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-14"
        >
          <div className="liquid-glass rounded-full px-4 py-1.5 inline-block mb-6">
            <span className="text-sm font-medium text-white font-body tracking-wide">
              Get in Touch
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading italic tracking-tight leading-[0.9] mb-6">
            Let's start a conversation.
          </h1>
          <p className="text-white/60 font-body font-light text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Ready to transform your business with AI, custom software, or a high-converting website? We're here to help.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Contact Info Side */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-col gap-8"
          >
            <h3 className="text-2xl md:text-3xl font-heading italic text-white">Contact Information</h3>
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 liquid-glass rounded-full flex items-center justify-center shrink-0 border border-white/5">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white/40 text-[11px] font-semibold uppercase tracking-widest mb-0.5">Email Us</p>
                  <a href="mailto:info@esyai.co.uk" className="text-white hover:text-white/70 transition-colors text-base font-light">
                    info@esyai.co.uk
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 liquid-glass rounded-full flex items-center justify-center shrink-0 border border-white/5">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white/40 text-[11px] font-semibold uppercase tracking-widest mb-0.5">Call Us</p>
                  <a href="tel:+447587583083" className="text-white hover:text-white/70 transition-colors text-base font-light block">
                    +44 7587 583083
                  </a>
                  <a href="tel:+447393080926" className="text-white hover:text-white/70 transition-colors text-base font-light block mt-1">
                    +44 7393 080926
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 liquid-glass rounded-full flex items-center justify-center shrink-0 border border-white/5">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white/40 text-[11px] font-semibold uppercase tracking-widest mb-0.5">Location</p>
                  <p className="text-white text-base font-light">
                    London, United Kingdom
                    <span className="text-white/40 ml-2 text-sm">· Available Worldwide</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="liquid-glass rounded-2xl p-6 border border-white/5">
              <h4 className="text-sm font-medium text-white mb-1.5">Office Hours</h4>
              <p className="text-white/50 font-light text-sm leading-relaxed">Monday – Friday · 9:00 AM – 6:00 PM GMT</p>
            </div>
          </motion.div>

          {/* Form Side */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="liquid-glass rounded-[2rem] p-8 md:p-10 border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] min-h-[500px] flex flex-col justify-center"
          >
            <AnimatePresence mode="wait">
              {isSubmitted ? (
                <motion.div 
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center space-y-6"
                >
                  <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <h3 className="text-3xl font-heading italic text-white">Message Sent!</h3>
                  <p className="text-white/60 font-light text-sm max-w-sm mx-auto">
                    Thanks for reaching out. We've received your message and will get back to you within 24 hours.
                  </p>
                  <button 
                    onClick={() => setIsSubmitted(false)}
                    className="mt-8 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-medium rounded-full transition-colors"
                  >
                    Send another message
                  </button>
                </motion.div>
              ) : (
                <motion.form 
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-5 w-full" 
                  onSubmit={handleSubmit}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="text-[11px] font-medium text-white/40 uppercase tracking-[0.15em] pl-1 mb-1.5 block">First Name</label>
                      <input 
                        type="text" 
                        id="firstName" 
                        required
                        className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-white/25 focus:bg-white/[0.05] transition-all"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="text-[11px] font-medium text-white/40 uppercase tracking-[0.15em] pl-1 mb-1.5 block">Last Name</label>
                      <input 
                        type="text" 
                        id="lastName" 
                        required
                        className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-white/25 focus:bg-white/[0.05] transition-all"
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="text-[11px] font-medium text-white/40 uppercase tracking-[0.15em] pl-1 mb-1.5 block">Email Address</label>
                    <input 
                      type="email" 
                      id="email" 
                      required
                      className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-white/25 focus:bg-white/[0.05] transition-all"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="service" className="text-[11px] font-medium text-white/40 uppercase tracking-[0.15em] pl-1 mb-1.5 block">How can we help?</label>
                    <select 
                      id="service"
                      required
                      className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-white/25 transition-all appearance-none cursor-pointer"
                      defaultValue=""
                    >
                      <option value="" disabled>Select a service...</option>
                      <option value="website">Website Development</option>
                      <option value="automation">Process Automation</option>
                      <option value="custom">Custom Web Application</option>
                      <option value="ai">AI Integration</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="text-[11px] font-medium text-white/40 uppercase tracking-[0.15em] pl-1 mb-1.5 block">Project Details</label>
                    <textarea 
                      id="message" 
                      rows={4}
                      required
                      className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-white/25 focus:bg-white/[0.05] transition-all resize-none"
                      placeholder="Tell us a bit about what you're looking to build..."
                    ></textarea>
                  </div>

                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full group relative flex items-center justify-center px-6 py-4 bg-white text-black font-semibold text-sm rounded-full overflow-hidden transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:hover:scale-100"
                  >
                    <span className="relative z-10 flex items-center gap-2 font-body tracking-wide">
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </span>
                    {!isSubmitting && (
                      <div className="absolute inset-0 bg-gradient-to-r from-gray-100 to-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    )}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
